import { useEffect } from 'react';
import { toast } from 'sonner';
import { takeSigninSignal } from '@/lib/api';

/**
 * One-shot welcome toast for a user's very first sign-in, so a first-time
 * visitor gets some confirmation instead of landing in an empty app. Returning
 * users get nothing: they asked to sign in and they are signed in.
 *
 * `signin=new` means "first sign-in ever", read from the durable
 * `users.first_signed_in_at` stamp — not "this callback inserted the row". The
 * two differ for anyone an admin pre-provisioned: their row is created at
 * invite time, so a creation-based signal skipped the very people the welcome
 * is most useful to. That is also why the copy welcomes rather than announcing
 * that an account was created; by the time an invited user arrives, it was
 * created days ago.
 *
 * The signal is stashed in sessionStorage by `captureAuthToken()`; this
 * hook drains it on mount so the toast fires exactly once per OAuth round
 * trip and never reappears on refresh.
 *
 * Mounted from `AuthedShell` so it runs as soon as the post-OAuth
 * landing finishes routing — every authenticated entry point sees it.
 */
export function useFirstSignInToast(): void {
  useEffect(() => {
    const signal = takeSigninSignal();
    if (signal?.signin === 'new') {
      toast.success('Welcome to Reflex.');
    }
  }, []);
}
